import { useEffect } from 'react';
import { usePredictionMarketEventsGetPriceHistory } from '@/lib/endpoints';
import type { PriceHistoryPointDto } from '@/lib/models';

export interface OutcomePriceHistoryLoaderProps {
  /** Primary outcome id whose Yes price history to load. */
  outcomeId: string;
  /** Window start (unix seconds). */
  startTs: number;
  /** Window end (unix seconds). */
  endTs: number;
  /** Bucket size in minutes (Plaee `period_interval`, max 1440). */
  periodInterval: number;
  /** Called with the loaded history for this outcome. */
  onLoaded: (outcomeId: string, history: PriceHistoryPointDto[]) => void;
}

/**
 * Headless loader for one outcome's price history (ARC-82 §5.2).
 *
 * Renders nothing; fetches via `usePredictionMarketEventsGetPriceHistory` and
 * reports the points up to the chart container in an effect. One instance is
 * mounted per market's primary outcome so a multi-market chart can fetch each
 * line without looping hooks.
 */
export function OutcomePriceHistoryLoader({
  outcomeId,
  startTs,
  endTs,
  periodInterval,
  onLoaded,
}: OutcomePriceHistoryLoaderProps) {
  const { data } = usePredictionMarketEventsGetPriceHistory(
    outcomeId,
    { startTs, endTs, periodInterval },
    { query: { enabled: Boolean(outcomeId) } },
  );

  const history = data?.history;
  useEffect(() => {
    if (history) onLoaded(outcomeId, history);
  }, [history, outcomeId, onLoaded]);

  return null;
}
